import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { describeProductDiff } from './productHistoryDiff';
import productApi from '@/lib/productApi';
import type { CatalogEvent } from '@/types/product';

const TYPE_LABELS: Record<string, string> = {
  created: 'Aangemaakt',
  imported: 'Geïmporteerd',
  draft_saved: 'Concept',
  published: 'Gepubliceerd',
  enabled: 'Ingeschakeld',
  disabled: 'Uitgeschakeld',
  archived: 'Gearchiveerd',
  unarchived: 'Uit archief gehaald',
  rate_changed: 'Tarief gewijzigd',
  rate_removed: 'Tarief verwijderd',
  component_updated: 'Onderdeel bijgewerkt',
};

export function ProductHistory({ productId }: { productId: string }) {
  const [events, setEvents] = useState<CatalogEvent[]>([]);

  useEffect(() => {
    productApi
      .history(productId)
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [productId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4" />
          History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nog geen wijzigingen vastgelegd.</p>
        ) : (
          <ol className="space-y-3">
            {events.map((event) => {
              const details = describeProductDiff(event);
              return (
                <li key={event.id} className="border-l-2 border-muted pl-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{TYPE_LABELS[event.type] ?? event.type}</Badge>
                    <span className="text-muted-foreground">
                      {new Date(event.createdAt).toLocaleString('nl-NL')}
                    </span>
                    <span className="font-medium">{event.actorName ?? 'Onbekend'}</span>
                  </div>
                  {event.reason && <p className="mt-1">{event.reason}</p>}
                  {details.length > 0 && (
                    <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                      {details.map((detail, index) => (
                        <li key={index} className="break-words">
                          {detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

export default ProductHistory;
